import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Trash2, Plus, Minus, ShoppingBag, Ticket, CheckCircle2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useRestaurantData } from '../hooks/useRestaurantData';
import './CartDrawer.css';

const CartDrawer = () => {
    const {
        cart,
        removeFromCart,
        updateQuantity,
        cartSubtotal,
        cartTotal,
        discountAmount,
        appliedVoucher,
        applyVoucher,
        removeVoucher,
        isCartOpen,
        setIsCartOpen,
        clearCart,
        earnPoints
    } = useCart();
    const data = useRestaurantData();
    const [voucherInput, setVoucherInput] = useState('');
    const [error, setError] = useState('');

    const handleApplyVoucher = () => {
        const voucher = (data.vouchers || []).find(v => v.code === voucherInput.toUpperCase() && v.active);
        if (voucher) {
            applyVoucher(voucher);
            setVoucherInput('');
            setError('');
        } else {
            setError('Code invalide ou expiré');
        }
    };

    const handleOrder = () => {
        const points = Math.floor(cartTotal);
        earnPoints(points);
        alert(`Commande confirmée ! Vous venez de gagner ${points} points Sugar Hut.`);
        clearCart();
        setIsCartOpen(false);
    };

    return (
        <AnimatePresence>
            {isCartOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsCartOpen(false)}
                        className="cart-overlay"
                    />

                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="cart-drawer"
                    >
                        <div className="cart-header">
                            <div className="cart-header-title">
                                <ShoppingBag size={24} className="text-primary" />
                                <h2>Mon Panier</h2>
                            </div>
                            <button onClick={() => setIsCartOpen(false)} className="close-cart-btn">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="cart-content">
                            {cart.length === 0 ? (
                                <div className="cart-empty">
                                    <div className="empty-icon-wrapper">
                                        <ShoppingBag size={48} className="text-gray-400" />
                                    </div>
                                    <p className="empty-text">Votre panier est vide pour le moment...</p>
                                    <button
                                        onClick={() => setIsCartOpen(false)}
                                        className="hero-cta"
                                        style={{ margin: '0 auto' }}
                                    >
                                        Découvrir la carte
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-4">
                                    {cart.map((item) => (
                                        <motion.div
                                            layout
                                            key={item.id}
                                            className="cart-item"
                                        >
                                            <div className="cart-item-info">
                                                <h4 className="cart-item-name">{item.name}</h4>
                                                <p className="cart-item-price">{item.price.toFixed(2)}€</p>

                                                <div className="quantity-controls">
                                                    <button
                                                        onClick={() => updateQuantity(item.id, -1)}
                                                        className="qty-btn"
                                                    >
                                                        <Minus size={14} />
                                                    </button>
                                                    <span className="item-qty">{item.quantity}</span>
                                                    <button
                                                        onClick={() => updateQuantity(item.id, 1)}
                                                        className="qty-btn"
                                                    >
                                                        <Plus size={14} />
                                                    </button>
                                                </div>
                                            </div>

                                            <button
                                                onClick={() => removeFromCart(item.id)}
                                                className="remove-item-btn"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </motion.div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {cart.length > 0 && (
                            <div className="cart-footer">
                                {/* Voucher Section */}
                                {!appliedVoucher ? (
                                    <div className="mb-4">
                                        <div className="flex gap-2">
                                            <div className="relative flex-1">
                                                <Ticket className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                                <input
                                                    className="w-full bg-gray/10 p-2 pl-10 rounded-lg outline-none focus:ring-1 focus:ring-primary text-sm"
                                                    placeholder="Code Promo"
                                                    value={voucherInput}
                                                    onChange={(e) => setVoucherInput(e.target.value)}
                                                />
                                            </div>
                                            <button
                                                onClick={handleApplyVoucher}
                                                className="bg-dark text-white px-4 rounded-lg text-sm font-bold"
                                            >
                                                Appliquer
                                            </button>
                                        </div>
                                        {error && <p className="text-red-500 text-[10px] mt-1 ml-1">{error}</p>}
                                    </div>
                                ) : (
                                    <div className="mb-4 p-2 bg-green-50 border border-green-200 rounded-lg flex justify-between items-center animate-fade-in">
                                        <div className="flex items-center gap-2 text-green-700">
                                            <CheckCircle2 size={16} />
                                            <span className="text-xs font-bold uppercase tracking-wider">Code {appliedVoucher.code} Appliqué !</span>
                                        </div>
                                        <button onClick={removeVoucher} className="text-green-700 hover:text-red-500">
                                            <X size={16} />
                                        </button>
                                    </div>
                                )}

                                <div className="space-y-2 mb-4 border-b border-gray pb-4">
                                    <div className="flex justify-between text-sm text-gray-500">
                                        <span>Sous-total</span>
                                        <span>{cartSubtotal.toFixed(2)}€</span>
                                    </div>
                                    {discountAmount > 0 && (
                                        <div className="flex justify-between text-sm text-green-600 font-bold">
                                            <span>Réduction</span>
                                            <span>-{discountAmount.toFixed(2)}€</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center text-lg font-black pt-2">
                                        <span>Total</span>
                                        <span className="text-primary">{cartTotal.toFixed(2)}€</span>
                                    </div>
                                </div>

                                <p className="cart-disclaimer mb-4">
                                    Gagnez <span className="font-black text-primary">{Math.floor(cartTotal)} points</span> avec cette commande !
                                </p>

                                <button
                                    className="checkout-btn mb-2"
                                    onClick={handleOrder}
                                >
                                    Confirmer la commande
                                </button>
                                <button
                                    onClick={clearCart}
                                    className="clear-cart-btn"
                                >
                                    Vider le panier
                                </button>
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default CartDrawer;
